import { Strings } from "../engine/strings/strings";
import type { CanvasLayer, FrameContext } from "./canvasLayer";
import { dpr } from "./dpr";
import {
	HUD_MESSAGE_ANNOUNCEMENT_DURATION_MS,
	HUD_MESSAGE_BG_COLOR,
	HUD_MESSAGE_BOTTOM_PADDING,
	HUD_MESSAGE_FADE_IN_MS,
	HUD_MESSAGE_FADE_OUT_MS,
	HUD_MESSAGE_FONT_SIZE,
	HUD_MESSAGE_HINT_DURATION_MS,
	HUD_MESSAGE_MIN_WIDTH,
	HUD_MESSAGE_PADDING_X,
	HUD_MESSAGE_SLIDE_DISTANCE,
	HUD_MESSAGE_SLOT_GAP,
	HUD_MESSAGE_SLOT_HEIGHT,
	HUD_MESSAGE_TEXT_COLOR,
} from "./hudMessageConstants";

export type HudMessageCategory = "announcement" | "prompt" | "hint";

export type HudMessageState = "fadingIn" | "visible" | "fadingOut";

export interface ActiveHudMessage {
	text: string;
	category: HudMessageCategory;
	durationMs: number | null;
	elapsedMs: number;
	state: HudMessageState;
	fadeMs: number;
}

function localizeOrFallback(stringKey: string, params?: unknown[]): string {
	const value = params && params.length > 0 ? Strings.get(stringKey, ...params) : Strings.get(stringKey);
	return value ?? stringKey;
}

export class HudMessageRenderer implements CanvasLayer {
	private messages: ActiveHudMessage[] = [];

	showAnnouncement(stringKey: string, params?: unknown[], durationMs?: number): void {
		const text = localizeOrFallback(stringKey, params);
		this.messages.push({
			text,
			category: "announcement",
			durationMs: durationMs ?? HUD_MESSAGE_ANNOUNCEMENT_DURATION_MS,
			elapsedMs: 0,
			state: "fadingIn",
			fadeMs: 0,
		});
	}

	showHint(stringKey: string, params?: unknown[]): void {
		const text = localizeOrFallback(stringKey, params);
		this.messages.push({
			text,
			category: "hint",
			durationMs: HUD_MESSAGE_HINT_DURATION_MS,
			elapsedMs: 0,
			state: "fadingIn",
			fadeMs: 0,
		});
	}

	setActivePrompts(texts: string[]): void {
		const desired = new Set(texts);
		const matched = new Set<string>();

		for (const message of this.messages) {
			if (message.category !== "prompt") continue;
			if (message.state === "fadingOut") continue;
			if (desired.has(message.text)) {
				matched.add(message.text);
			} else {
				message.state = "fadingOut";
				message.fadeMs = 0;
			}
		}

		for (const text of texts) {
			if (matched.has(text)) continue;
			const alreadyLive = this.messages.some(
				(m) => m.category === "prompt" && m.state !== "fadingOut" && m.text === text,
			);
			if (alreadyLive) continue;
			this.messages.push({
				text,
				category: "prompt",
				durationMs: null,
				elapsedMs: 0,
				state: "fadingIn",
				fadeMs: 0,
			});
		}
	}

	passTime(elapsedMs: number): void {
		for (const message of this.messages) {
			message.elapsedMs += elapsedMs;
			if (message.state === "fadingIn") {
				message.fadeMs += elapsedMs;
				if (message.fadeMs >= HUD_MESSAGE_FADE_IN_MS) {
					message.state = "visible";
					message.fadeMs = 0;
				}
			} else if (message.state === "visible") {
				if (message.durationMs !== null) {
					message.fadeMs += elapsedMs;
					if (message.fadeMs >= message.durationMs) {
						message.state = "fadingOut";
						message.fadeMs = 0;
					}
				}
			} else if (message.state === "fadingOut") {
				message.fadeMs += elapsedMs;
			}
		}
		this.messages = this.messages.filter(
			(m) => !(m.state === "fadingOut" && m.fadeMs >= HUD_MESSAGE_FADE_OUT_MS),
		);
	}

	render(ctx: CanvasRenderingContext2D, _frame: FrameContext): void {
		if (this.messages.length === 0) return;

		const ratio = dpr();
		const fontSize = Math.round(HUD_MESSAGE_FONT_SIZE * ratio);
		const slotHeight = Math.round(HUD_MESSAGE_SLOT_HEIGHT * ratio);
		const slotGap = Math.round(HUD_MESSAGE_SLOT_GAP * ratio);
		const bottomPadding = Math.round(HUD_MESSAGE_BOTTOM_PADDING * ratio);
		const paddingX = Math.round(HUD_MESSAGE_PADDING_X * ratio);
		const minWidth = Math.round(HUD_MESSAGE_MIN_WIDTH * ratio);

		const canvasWidth = ctx.canvas.width;
		const canvasHeight = ctx.canvas.height;

		ctx.save();
		ctx.font = `${fontSize}px monospace`;
		ctx.textAlign = "center";
		ctx.textBaseline = "middle";

		// Render newest at the bottom: iterate from end of list (newest) at slot 0,
		// older messages stack upward in higher slot indices.
		for (let i = this.messages.length - 1; i >= 0; i--) {
			const message = this.messages[i];
			const slotIndex = this.messages.length - 1 - i;
			const alpha = this.computeAlpha(message);
			if (alpha <= 0) continue;

			const textWidth = ctx.measureText(message.text).width;
			const bgWidth = Math.max(textWidth + paddingX * 2, minWidth);
			const bgHeight = slotHeight;
			const bgX = Math.round((canvasWidth - bgWidth) / 2);
			const slideOffset = Math.round(this.computeSlideOffset(message) * ratio);
			const bgY =
				canvasHeight - bottomPadding - (slotIndex + 1) * bgHeight - slotIndex * slotGap + slideOffset;

			ctx.globalAlpha = alpha;
			ctx.fillStyle = HUD_MESSAGE_BG_COLOR;
			ctx.fillRect(bgX, bgY, bgWidth, bgHeight);

			ctx.fillStyle = HUD_MESSAGE_TEXT_COLOR;
			const textX = bgX + bgWidth / 2;
			const textY = bgY + bgHeight / 2;
			ctx.fillText(message.text, textX, textY);
		}

		ctx.restore();
	}

	clear(): void {
		this.messages = [];
	}

	hasMessages(): boolean {
		return this.messages.length > 0;
	}

	// Test-only: exposes internal message list for assertions in unit tests.
	getMessagesForTest(): ReadonlyArray<ActiveHudMessage> {
		return this.messages;
	}

	private computeAlpha(message: ActiveHudMessage): number {
		if (message.state === "fadingIn") {
			if (HUD_MESSAGE_FADE_IN_MS <= 0) return 1;
			return Math.min(1, message.fadeMs / HUD_MESSAGE_FADE_IN_MS);
		}
		if (message.state === "fadingOut") {
			if (HUD_MESSAGE_FADE_OUT_MS <= 0) return 0;
			return Math.max(0, 1 - message.fadeMs / HUD_MESSAGE_FADE_OUT_MS);
		}
		return 1;
	}

	// Returns a downward Y offset (in CSS pixels) used for the slide animation.
	// Messages enter sliding up from below their resting slot and exit sliding back down.
	private computeSlideOffset(message: ActiveHudMessage): number {
		if (message.state === "fadingIn") {
			if (HUD_MESSAGE_FADE_IN_MS <= 0) return 0;
			const progress = Math.min(1, message.fadeMs / HUD_MESSAGE_FADE_IN_MS);
			return (1 - progress) * HUD_MESSAGE_SLIDE_DISTANCE;
		}
		if (message.state === "fadingOut") {
			if (HUD_MESSAGE_FADE_OUT_MS <= 0) return HUD_MESSAGE_SLIDE_DISTANCE;
			const progress = Math.min(1, message.fadeMs / HUD_MESSAGE_FADE_OUT_MS);
			return progress * HUD_MESSAGE_SLIDE_DISTANCE;
		}
		return 0;
	}

}
